import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { API_BASE } from '../../lib/utils';
import { CheckCircle, Circle, ArrowRight, Lock } from 'lucide-react';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [personalityStatus, setPersonalityStatus] = useState(null);
  const [careerDiscoveryStatus, setCareerDiscoveryStatus] = useState(null);
  const [testingStatus, setTestingStatus] = useState(null);
  const [preferencesStatus, setPreferencesStatus] = useState(null);
  const [bookingStatus, setBookingStatus] = useState(null);
  const [finalDocStatus, setFinalDocStatus] = useState(null);
  const [cvData, setCvData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profileRes, personalityRes, discoveryRes, cvRes, testingRes, prefsRes, bookingRes, finalRes] = await Promise.all([
          axios.get(`${API_BASE}/apprentice/profile`, { params: { user_id: user.id } }).catch(() => ({ data: null })),
          axios.get(`${API_BASE}/personality-profile/status/${user.id}`).catch(() => ({ data: { completed: false } })),
          axios.get(`${API_BASE}/career-discovery/status/${user.id}`).catch(() => ({ data: { completed: false } })),
          axios.get(`${API_BASE}/resume/${user.id}`).catch(() => ({ data: null })),
          axios.get(`${API_BASE}/testing/status/${user.id}`).catch(() => ({ data: { completed: false } })),
          axios.get(`${API_BASE}/preferences/status/${user.id}`).catch(() => ({ data: { completed: false } })),
          axios.get(`${API_BASE}/booking/status/${user.id}`).catch(() => ({ data: { completed: false } })),
          axios.get(`${API_BASE}/candidate-report/final/${user.id}`).catch(() => ({ data: { has_final: false } })),
        ]);
        setProfile(profileRes.data);
        setPersonalityStatus(personalityRes.data);
        setCareerDiscoveryStatus(discoveryRes.data);
        setCvData(cvRes.data);
        setTestingStatus(testingRes.data);
        setPreferencesStatus(prefsRes.data);
        setBookingStatus(bookingRes.data);
        setFinalDocStatus(finalRes.data);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user.id]);

  const registrationComplete = profile?.name && profile?.location && profile?.has_paid;
  const resumeComplete = cvData && cvData.is_complete === true;
  const personalityComplete = personalityStatus?.completed === true;
  const careerDiscoveryComplete = careerDiscoveryStatus?.completed === true;
  const preferencesComplete = preferencesStatus?.completed === true;
  const careerPrefsComplete = careerDiscoveryComplete && preferencesComplete;
  const testingComplete = testingStatus?.completed === true;
  const bookingComplete = bookingStatus?.completed === true;
  const finalDocComplete = finalDocStatus?.has_final === true;

  const steps = [
    {
      id: 1,
      title: 'Registration',
      description: 'Complete your profile and payment to get started',
      completed: registrationComplete,
      path: '/apprentice/profile',
      locked: false
    },
    {
      id: 2,
      title: 'Resume/CV Builder',
      description: 'Build your standardised CV using the guided compiler',
      completed: resumeComplete,
      path: '/apprentice/cv',
      locked: !registrationComplete
    },
    {
      id: 3,
      title: 'Personality Profile',
      description: '50 image-based questions across 5 dimensions to understand your strengths',
      completed: personalityComplete,
      path: '/apprentice/personality',
      locked: !resumeComplete
    },
    {
      id: 4,
      title: 'Career Preferences',
      description: 'Pick the industries, roles, county and top 5 employers that excite you',
      completed: careerPrefsComplete,
      path: '/apprentice/career-preferences',
      locked: !personalityComplete
    },
    {
      id: 5,
      title: 'Aptitude Tests',
      description: 'Complete personalised assessments based on your career selections',
      completed: testingComplete,
      path: '/apprentice/assessments',
      locked: !careerPrefsComplete
    },
    {
      id: 6,
      title: 'Career Counsellor Booking',
      description: 'Book a 1.5 hour mock Zoom call with a career counsellor',
      completed: bookingComplete,
      path: '/apprentice/booking',
      locked: !testingComplete
    },
    {
      id: 7,
      title: 'Final Documentation',
      description: 'Your complete candidate report with counsellor recommendation',
      completed: finalDocComplete,
      path: '/apprentice/final-doc',
      locked: !bookingComplete
    },
  ];

  if (loading) {
    return (
      <div className="p-12 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0071e3]"></div>
      </div>
    );
  }

  const completedSteps = steps.filter(s => s.completed).length;

  return (
    <div className="p-4 sm:p-8 md:p-12">
      <div className="max-w-6xl mx-auto">
        {/* Welcome Header */}
        <div className="mb-8 sm:mb-12">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-semibold text-[#1d1d1f] mb-2 sm:mb-3 break-words" data-testid="dashboard-welcome">
            Welcome back, {user.name.split(' ')[0]}
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-[#86868b]">Let's continue building your apprenticeship journey</p>
        </div>

        {/* Progress Tracker */}
        <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 mb-6 sm:mb-8 border border-[#d2d2d7]">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8 gap-2">
            <h2 className="text-2xl sm:text-3xl font-semibold text-[#1d1d1f]">Your Progress</h2>
            <div className="text-sm sm:text-base text-[#86868b]">
              {completedSteps} of {steps.length} completed
            </div>
          </div>

          {/* Progress bar */}
          <div className="w-full h-2 bg-[#f5f5f7] rounded-full overflow-hidden mb-6">
            <div
              className="h-full bg-[#34C759] rounded-full transition-all duration-700"
              style={{ width: `${(completedSteps / steps.length) * 100}%` }}
            />
          </div>

          <div className="space-y-3 sm:space-y-4">
            {steps.map((step, idx) => (
              <div
                key={step.id}
                className={`flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 p-4 sm:p-6 rounded-xl sm:rounded-2xl transition-all ${
                  step.completed
                    ? 'bg-green-50'
                    : step.locked
                    ? 'bg-[#f5f5f7] opacity-50'
                    : 'bg-blue-50'
                }`}
                data-testid={`progress-step-${step.id}`}
              >
                <div className="flex items-start sm:items-center space-x-3 sm:space-x-4 flex-1">
                  <div className="flex-shrink-0 flex items-center gap-2">
                    <span className="text-xs font-bold text-[#86868b] w-5">{step.id}</span>
                    {step.completed ? (
                      <CheckCircle className="w-6 h-6 sm:w-7 sm:h-7 text-green-600" />
                    ) : step.locked ? (
                      <Lock className="w-6 h-6 sm:w-7 sm:h-7 text-[#86868b]" />
                    ) : (
                      <Circle className="w-6 h-6 sm:w-7 sm:h-7 text-[#0071e3]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg sm:text-xl font-semibold text-[#1d1d1f] break-words">{step.title}</h3>
                    <p className="text-sm sm:text-base text-[#86868b] break-words">{step.description}</p>
                  </div>
                </div>
                {!step.completed && !step.locked && (
                  <Button
                    onClick={() => navigate(step.path)}
                    className="bg-[#0071e3] hover:bg-[#0077ed] text-white rounded-full px-4 sm:px-6 w-full sm:w-auto text-sm sm:text-base"
                    data-testid={`continue-step-${step.id}-btn`}
                  >
                    {idx === 0 ? 'Get Started' : 'Continue'}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
          <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-[#d2d2d7]">
            <div className="text-xs sm:text-sm text-[#86868b] mb-2">Registration</div>
            <div className={`text-2xl sm:text-3xl font-semibold ${registrationComplete ? 'text-green-600' : 'text-[#FF9500]'}`}>
              {registrationComplete ? 'Complete' : 'Pending'}
            </div>
          </div>
          <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-[#d2d2d7]">
            <div className="text-xs sm:text-sm text-[#86868b] mb-2">Aptitude Tests</div>
            <div className={`text-2xl sm:text-3xl font-semibold ${testingComplete ? 'text-green-600' : 'text-[#FF9500]'}`}>
              {testingComplete ? 'Complete' : 'Pending'}
            </div>
          </div>
          <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-[#d2d2d7]">
            <div className="text-xs sm:text-sm text-[#86868b] mb-2">Counsellor Booking</div>
            <div className={`text-2xl sm:text-3xl font-semibold ${bookingComplete ? 'text-green-600' : 'text-[#FF9500]'}`}>
              {bookingComplete ? 'Booked' : 'Pending'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
